// conversationAdapter/waiting — the messages sent while the assistant works
// (PS-61), projected from the conversation: waiting ones and just-deleted
// ones (Undo) as author entries with a `status`, read ones as ordinary
// messages, and what changed because of a read batch. Pure + total, same
// wire-shape readers as the rest of the codec.

export { queuedEntries, waitingChanges, waitingEntries };

import type {
    AuthorEntry,
    AuthorEntryStatus,
    ChatEntry,
} from "../../components/Chat/types.js";
import { fields, list, num, str } from "../wireShape.js";
import { textOf } from "./text.js";

// `batchId → summary` of the `waiting_change_marker` artifacts.
const waitingChanges = (
    active: ReadonlyMap<string, unknown> | undefined,
): ReadonlyMap<string, string> =>
    new Map(
        list(active?.get("artifacts")).flatMap((raw) => {
            const a = fields(raw);
            const batchId = str(a?.get("batchId"));
            const summary = str(a?.get("summary"));
            return str(a?.get("type")) === "waiting_change_marker" &&
                batchId !== undefined &&
                summary !== undefined
                ? [[batchId, summary] as const]
                : [];
        }),
    );

// The messages that still wait, with any just deleted (Undo) back in the
// place they had, last in the thread: the chat shows them under the running
// step (PS-61).
const queuedEntries = (
    active: ReadonlyMap<string, unknown> | undefined,
    timeOf: (messageId: string) => number | undefined,
): readonly ChatEntry[] => {
    const queued: ChatEntry[] = [
        ...waitingEntries(active?.get("waiting"), { type: "waiting" }, timeOf),
    ];
    const deleted = list(active?.get("deletedWaiting"))
        .map((raw) => ({
            index: num(fields(raw)?.get("index")) ?? Number.MAX_SAFE_INTEGER,
            entry: waitingEntries(
                [fields(raw)?.get("message")],
                { type: "deleted" },
                timeOf,
            )[0],
        }))
        .sort((a, b) => a.index - b.index);
    for (const d of deleted)
        if (d.entry !== undefined)
            queued.splice(Math.min(d.index, queued.length), 0, d.entry);
    return queued;
};

// Waiting messages (`{messageId, content}`) as author entries with `status`.
const waitingEntries = (
    raw: unknown,
    status: AuthorEntryStatus,
    timeOf: (messageId: string) => number | undefined,
): readonly AuthorEntry[] =>
    list(raw).flatMap((m) => {
        const f = fields(m);
        const id = str(f?.get("messageId"));
        if (id === undefined) return [];
        const time = timeOf(id);
        return [
            {
                type: "author",
                id,
                text: textOf(f?.get("content")),
                status,
                ...(time === undefined ? {} : { timestamp: time }),
            },
        ];
    });
